import { memo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import { Button, Chip, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Link } from '@mint/routing';

import { INTEGRATION_TYPES_NEW } from '../../../constants/ComponentsConstants';
import { useIntegrationsPermissions } from '../../../hooks/index.js';
import { useIntegrationPaths } from '../../../hooks/useIntegrationsRouteApi.js';
import { Platform } from '../../../types/index.js';
import PlatformIcon from '../../_atoms/PlatformIcon/index.js';

type Props = {
  integration: Platform;
};

function IntegrationCardOmni({ integration }: Readonly<Props>) {
  const { t } = useTranslation('@myn/integrations_v2');
  const {
    platform,
    category,
    isIntegrated,
    logo,
    integrationsCount,
    type,
    label,
  } = integration;
  const isAllowed = useIntegrationsPermissions('view', category);
  const isUserReadOnly = !useIsAllowedPermission('integrations:modify');
  const chipProps = INTEGRATION_TYPES_NEW(t)[type]?.chipProps;
  const paths = useIntegrationPaths();

  return (
    <div
      data-testid={integration.id}
      aria-label="platform card"
      className={cn(
        'box-border flex flex-col items-start rounded-sm bg-ui-secondary p-6 no-underline',
        isIntegrated
          ? 'border-2 border-success-main'
          : 'border border-ui-active',
        !isAllowed && 'pointer-events-none opacity-60',
      )}
    >
      <div className="mb-4 flex w-full justify-between">
        <PlatformIcon logo={logo} counter={integrationsCount ?? undefined} />
        <Chip color={chipProps.color}>
          {chipProps.icon}
          {chipProps.label}
        </Chip>
      </div>
      <h4 className={cn(typographyVariants({ variant: 'h4' }), 'mb-1 h-18')}>
        {label}
      </h4>
      <Link
        to={paths.connectionsAdd}
        preload="intent"
        params={{
          integration: platform,
        }}
        style={{
          textDecoration: 'none',
          width: '100%',
        }}
        data-dd-action-name="open connection integration"
      >
        {!isIntegrated ? (
          <Button
            color="secondary"
            className="w-full"
            disabled={isUserReadOnly}
            data-dd-action-name="integrate omni connection"
          >
            <Add className="mr-2" />
            {t('Integrate')}
          </Button>
        ) : (
          <Button
            disabled={!isAllowed}
            variant="outlined"
            className="w-full whitespace-nowrap"
            data-dd-action-name="manage omni connection"
          >
            {t('Manage Integration')}
          </Button>
        )}
      </Link>
    </div>
  );
}

export default memo(IntegrationCardOmni);
